import { Content, Horizontal, StretchSpacer } from "gls/lib";
import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { colours, coloursNames } from "styling/colours";
import { classes, style } from "typestyle";

export interface Context {
  readonly selectedTab?: string;
  readonly setSelectedTab: (tabId: string) => void;
}

export const TabsContext = React.createContext<Context>({
  selectedTab: "",
  setSelectedTab: () => { },
});

type TabsProps = {
  defaultTabId?: string;
  children: React.ReactNode;
  className?: string;
  tabsClassName?: string;
  smallBorder?: boolean;
  onTabChange?: (tabId: string) => void;
  expandTabs?: boolean;
  scrollSelectedTab?: boolean;
  headerComponent?: ReactNode;
};

export const Tabs = (props: TabsProps) => {
  const [selectedTab, setSelectedTab] = useState(props.defaultTabId);
  const [tabs, setTabs] = useState<React.ReactElement<TabProps>[]>([]);
  const [isDefaultPosition, setDefaultPosition] = useState(false);
  const { Provider } = TabsContext;

  useEffect(() => {
    const foundTabs: React.ReactElement<TabProps>[] = [];

    React.Children.forEach(props.children, (child) => {
      // Child is a React element which contains type.name
      const typeName = (child as any)?.type?.name;
      // If child is of type Tab it must have a tabId
      const hasTabId = (child as React.ReactElement<TabProps>)?.props?.tabId;

      if (typeName === Tab.name && hasTabId) {
        foundTabs.push(child as React.ReactElement<TabProps>);
      }
    });

    let currentTab = selectedTab;
    if (foundTabs && foundTabs.length && !currentTab) {
      currentTab = foundTabs[0].props.tabId;
      setSelectedTab(currentTab);
    }

    if (!isDefaultPosition && props.scrollSelectedTab) {
      setTimeout(() => {
        const tab = document.getElementById(currentTab || "");
        tab?.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
        setDefaultPosition(true);
      }, 100);
    }

    setTabs(foundTabs);
    // eslint-disable-next-line
  }, [props.children]);

  return (
    <Provider value={{ selectedTab, setSelectedTab }}>
      <TabsPanel
        tabs={tabs}
        tabsClassName={props.tabsClassName}
        className={props.className}
        onTabChange={props.onTabChange}
        smallBorder={props.smallBorder}
        expandTabs={props.expandTabs}
        scrollSelectedTab={props.scrollSelectedTab}
        headerComponent={props.headerComponent}
      />
      {props.children}
    </Provider>
  );
};

type TabsPanelProps = {
  tabs?: React.ReactElement<TabProps>[];
  tabsClassName?: string;
  className?: string;
  smallBorder?: boolean;
  onTabChange?: (tabId: string) => void;
  expandTabs?: boolean;
  scrollSelectedTab?: boolean;
  headerComponent?: ReactNode;
};

const TabsPanel = (props: TabsPanelProps) => {
  const { tabs } = props;
  const { selectedTab, setSelectedTab } = useContext(TabsContext);
  const ref = useRef<HTMLDivElement>(null);
  let result = null;

  const handleTabSelect = (tabId: string) => {
    setSelectedTab(tabId);
    props.onTabChange && props.onTabChange(tabId);

    if (props.scrollSelectedTab) {
      const tab = document.getElementById(tabId);
      tab?.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  if (tabs && tabs.length) {
    result = tabs.map((tab, index) => {
      const { tabId } = tab.props;
      const isTabSelected = selectedTab === tab.props.tabId;

      return (
        <Content
          role="button"
          tabIndex={0}
          id={tab.props.tabId}
          key={`tabs-panel-${tab.key}-${index}`}
          onKeyDown={(e) => e.key === "Enter" && handleTabSelect(tabId)}
          onClick={() => handleTabSelect(tabId)}
          verticalAlign="center"
          horizontalAlign="center"
          className={classes(
            tabStyle(tabs.length, props.smallBorder, props.expandTabs),
            isTabSelected && tabSelectedStyle(props.smallBorder),
            props.tabsClassName
          )}
          aria-label={tab.props.ariaLabel || tab.props.tabId}
        >
          <span>{tab.props.title || tab.props.tabId}</span>
        </Content>
      );
    });
  }

  return (
    <Content className={classes(wrapperStyle, props.className)}>
      <Horizontal
        ref={ref}
        tabIndex={-1}
        spacing={0}
        className={classes(tabsContainerStyle, props.tabsClassName)}
        style={{ borderBottom: "1px solid #eee" }}
      >
        {result}
        {props.headerComponent && (
          <>
            <StretchSpacer />
            {props.headerComponent}
          </>
        )}
      </Horizontal>
    </Content>
  );
};

type TabProps = {
  children: React.ReactNode;
  title?: React.ReactNode | string;
  tabId: string;
  ariaLabel?: string;
};

export const Tab = (props: TabProps) => {
  const { tabId, children } = props;
  const { selectedTab } = useContext(TabsContext);
  let result = null;

  if (selectedTab === tabId) {
    result = <>{children}</>;
  }

  return result;
};

const tabStyle = (
  length: number,
  smallBorder?: boolean,
  expandTabs?: boolean
) =>
  style({
    fontFamily: "MuseoSlab",
    width: expandTabs ? `calc(100%/${length})` : "fit-content",
    marginRight: smallBorder ? "1.3em" : 0,
    padding: smallBorder ? 0 : "8px 3px",
    cursor: "pointer",
    color: colours.secondaryText,
    borderBottom: smallBorder ? undefined : "3px solid transparent",
    $nest: {
      "svg g path": {
        fill: colours.secondaryText,
      },
      span: {
        padding: smallBorder ? "8px 3px" : 0,
        borderBottom: smallBorder ? "3px solid transparent" : undefined,
      },
      ".tab-sub-title": {
        padding: "4px",
        backgroundColor: "#EBECED",
        borderRadius: '3px',
      },
      "&:focus-visible": {
        outline: `1px solid ${coloursNames.deepSkyBlue}`,
        outlineOffset: "-1px"
      }
    },
  });

const tabSelectedStyle = (smallBorder?: boolean) =>
  style({
    color: `${colours.link} !important`,
    background: colours.background,
    borderColor: `${colours.secondary} !important`,
    $nest: {
      "svg g path": {
        fill: `${colours.secondary} !important`,
      },
      span: {
        borderColor: smallBorder ? colours.secondary : undefined,
      },
      ".tab-sub-title": {
        padding: "4px",
        backgroundColor: colours.blueBackground,
        borderRadius: '3px',
      }
    },
  });

const wrapperStyle = style({
  $nest: {
    "::-webkit-scrollbar": {
      width: "0",
      height: "0",
    },
  },
});

const tabsContainerStyle = style({
  height: "fit-content",
  overflow: "auto",
  width: "100%",
});
